import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useEffect, useMemo, useRef, useState, Suspense, lazy, useCallback, } from "react";
import { useRecordContext, useSidebarState, RecordContextProvider } from "react-admin";
import ElectionHeader from "@/components/ElectionHeader";
import { AuthContext } from "@/providers/AuthContextProvider";
import { IPermissions } from "@/types/keycloak";
import { useTranslation } from "react-i18next";
import { EventTimeZoneProvider } from "@/providers/EventTimeZoneProvider";
import { FailedSealsBanner } from "@/components/dashboard/FailedSealsBanner";
import { useElectionEventTallyStore } from "@/providers/ElectionEventTallyProvider";
import { v4 as uuidv4 } from "uuid";
import { EPublishType } from "../Publish/EPublishType";
import { EElectionEventLockedDown, EVoterCertificatePolicy, } from "@sequentech/ui-core";
import { Alert, Box, CircularProgress } from "@mui/material";
import { Tabs } from "@/components/Tabs";
import { useNavigate, useLocation } from "react-router-dom";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { LOGS_TAB_ID } from "@/components/logs/messagingLogFilters";
import { showMessagingTab } from "./messagingTabAccess";
import { LockdownBanner } from "./LockdownBanner";
import { ElectionEventDataTab } from "./ElectionEventDataTab";
import { ElectionEventDashboardTab as DashboardTab } from "./ElectionEventDashboardTab";
import { EEventDataMode, useElectionEventDataMode } from "./useElectionEventDataMode";
// ---------------------------------------------------------------------
// Lazy load all tab contents
// ---------------------------------------------------------------------
var EditElectionEventTextData = lazy(function () {
    return import("./EditElectionEventTextData").then(function (m) { return ({ default: m.default }); });
});
var EditElectionEventUsers = lazy(function () {
    return import("./EditElectionEventUsers").then(function (m) { return ({ default: m.EditElectionEventUsers }); });
});
var EditElectionEventMessaging = lazy(function () {
    return import("./EditElectionEventMessaging").then(function (m) { return ({ default: m.EditElectionEventMessaging }); });
});
var EditElectionEventAreas = lazy(function () {
    return import("./EditElectionEventAreas").then(function (m) { return ({ default: m.EditElectionEventAreas }); });
});
var EditElectionEventKeys = lazy(function () {
    return import("./EditElectionEventKeys").then(function (m) { return ({ default: m.EditElectionEventKeys }); });
});
var EditElectionEventSignatures = lazy(function () {
    return import("./Signatures/EditElectionEventSignatures").then(function (m) { return ({
        default: m.EditElectionEventSignatures,
    }); });
});
var EditElectionEventCAs = lazy(function () {
    return import("./EditElectionEventCAs").then(function (m) { return ({ default: m.EditElectionEventCAs }); });
});
var EditElectionEventIvr = lazy(function () {
    return import("./EditElectionEventIvr").then(function (m) { return ({ default: m.EditElectionEventIvr }); });
});
var EditElectionEventTally = lazy(function () {
    return import("./EditElectionEventTally").then(function (m) { return ({ default: m.EditElectionEventTally }); });
});
var TallySheetImports = lazy(function () {
    return import("../TallySheetImport/TallySheetImports").then(function (m) { return ({
        default: m.TallySheetImports,
    }); });
});
var Publish = lazy(function () {
    return import("@/resources/Publish/Publish").then(function (m) { return ({ default: m.Publish }); });
});
var ElectoralLog = lazy(function () { return import("./ElectoralLog").then(function (m) { return ({ default: m.ElectoralLog }); }); });
var EditElectionEventTasks = lazy(function () {
    return import("./EditElectionEventTasks").then(function (m) { return ({ default: m.EditElectionEventTasks }); });
});
var EditElectionEventEvents = lazy(function () {
    return import("./EditElectionEventScheduledEvents").then(function (m) { return ({
        default: m.EditElectionEventEvents,
    }); });
});
var EditElectionEventApprovals = lazy(function () {
    return import("./EditElectionEventApprovals").then(function (m) { return ({
        default: m.EditElectionEventApprovals,
    }); });
});
var EditElectionEventReports = lazy(function () {
    return import("../Reports/EditReportsTab").then(function (m) { return ({ default: m.EditReportsTab }); });
});
// ---------------------------------------------------------------------
// Stable Tab Components (all use useRecordContext)
// ---------------------------------------------------------------------
var LocalizationTab = function () { return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Localization..." }), children: _jsx(EditElectionEventTextData, {}) })); };
var VotersTab = function () {
    var record = useRecordContext();
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Voters..." }), children: _jsx(EditElectionEventUsers, {}) }));
};
var MessagingTab = function () {
    var t = useTranslation().t;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: t("common.label.loadingData") }), children: _jsx(EditElectionEventMessaging, {}) }));
};
var AreasTab = function () { return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Areas..." }), children: _jsx(EditElectionEventAreas, {}) })); };
var KeysTab = function (_a) {
    var showKeysList = _a.showKeysList;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Keys..." }), children: _jsx(EditElectionEventKeys, { isShowCeremony: showKeysList, isShowTrusteeCeremony: showKeysList }) }));
};
var SignaturesTab = function () {
    var t = useTranslation().t;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: t("common.label.loadingData") }), children: _jsx(EditElectionEventSignatures, {}) }));
};
var CAsTab = function () {
    var t = useTranslation().t;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: t("common.label.loadingData") }), children: _jsx(EditElectionEventCAs, {}) }));
};
var IvrTab = function () {
    var t = useTranslation().t;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: t("common.label.loadingData") }), children: _jsx(EditElectionEventIvr, {}) }));
};
var TallyTab = function () { return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Tally..." }), children: _jsx(EditElectionEventTally, {}) })); };
var TallySheetImportsTab = function (_a) {
    var openImportId = _a.openImportId, onOpenImportHandled = _a.onOpenImportHandled;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Tally Sheet Imports..." }), children: _jsx(TallySheetImports, { openImportId: openImportId, onOpenImportHandled: onOpenImportHandled }) }));
};
var PublishTab = function (_a) {
    var showList = _a.showList;
    var record = useRecordContext();
    return (_jsxs(Suspense, { fallback: _jsx("div", { children: "Loading Publish..." }), children: [_jsx(FailedSealsBanner, { electionEventId: record === null || record === void 0 ? void 0 : record.id, presentation: record === null || record === void 0 ? void 0 : record.presentation }), _jsx(Publish, { electionEventId: record === null || record === void 0 ? void 0 : record.id, type: EPublishType.Event, showList: showList })] }));
};
var TasksTab = function (_a) {
    var showList = _a.showList;
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Tasks..." }), children: _jsx(EditElectionEventTasks, { showList: showList }) }));
};
var LogsTab = function () { return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Logs..." }), children: _jsx(ElectoralLog, {}) })); };
var EventsTab = function () {
    var record = useRecordContext();
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Events..." }), children: _jsx(EditElectionEventEvents, { electionEventId: record === null || record === void 0 ? void 0 : record.id }) }));
};
var ReportsTab = function () {
    var record = useRecordContext();
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Reports..." }), children: _jsx(EditElectionEventReports, { electionEventId: record === null || record === void 0 ? void 0 : record.id }) }));
};
var ApprovalsTab = function (_a) {
    var showList = _a.showList;
    var record = useRecordContext();
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Approvals..." }), children: _jsx(EditElectionEventApprovals, { electionEventId: record === null || record === void 0 ? void 0 : record.id, showList: showList }) }));
};
// ---------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------
export var ElectionEventTabs = function () {
    var _a, _b, _c, _d;
    var record = useRecordContext();
    var authContext = useContext(AuthContext);
    var dataMode = useElectionEventDataMode(record);
    var t = useTranslation().t;
    var location = useLocation();
    var navigate = useNavigate();
    var refreshRef = useRef(null);
    var setTallyId = useElectionEventTallyStore().setTallyId;
    var open = useSidebarState()[0];
    var aliasRenderer = useAliasRenderer();
    // State for tab-specific triggers
    var _e = useState(null), showKeysList = _e[0], setShowKeysList = _e[1];
    var _f = useState(), showTaskList = _f[0], setShowTaskList = _f[1];
    var _g = useState(), showPublishList = _g[0], setShowPublishList = _g[1];
    var _h = useState(), showApprovalList = _h[0], setShowApprovalList = _h[1];
    var _j = useState(function () {
        var baseUrl = new URL(window.location.href);
        return baseUrl.searchParams.get("tallySheetImportId");
    }), pendingTallySheetImportId = _j[0], setPendingTallySheetImportId = _j[1];
    var _k = useState(function () {
        var _a;
        var baseUrl = new URL(window.location.href);
        return Number.parseInt((_a = baseUrl.searchParams.get("tabIndex")) !== null && _a !== void 0 ? _a : "0");
    }), selectedTab = _k[0], setSelectedTab = _k[1];
    var clearPendingTallySheetImportId = useCallback(function () {
        setPendingTallySheetImportId(null);
        var searchParams = new URLSearchParams(location.search);
        if (searchParams.has("tallySheetImportId")) {
            searchParams.delete("tallySheetImportId");
            var search = searchParams.toString();
            navigate({ pathname: location.pathname, search: search ? "?".concat(search) : "" }, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);
    // Dashboard refresh logic// Dashboard refresh logic
    var _l = useState(0), loadedChildren = _l[0], setLoadedChildren = _l[1];
    // MEMOIZE THIS!
    var handleChildMount = useCallback(function () {
        setLoadedChildren(function (prev) { return (prev < 2 ? prev + 1 : prev); });
    }, []);
    useEffect(function () {
        var _a;
        if (loadedChildren === 1 || loadedChildren === 2) {
            (_a = refreshRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
    }, [loadedChildren]);
    // Clean URL on mount
    useEffect(function () {
        if (record) {
            var basePath = location.pathname.split("/").slice(0, 3).join("/");
            navigate(basePath);
        }
    }, [location.pathname, navigate, record]);
    // Permission checks
    var isElectionEventLocked = ((_a = record === null || record === void 0 ? void 0 : record.presentation) === null || _a === void 0 ? void 0 : _a.locked_down) === EElectionEventLockedDown.LOCKED_DOWN;
    var showDashboard = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ADMIN_DASHBOARD_VIEW);
    var showData = dataMode === EEventDataMode.EDIT || dataMode === EEventDataMode.LOCKED_DOWN;
    var showTextData = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_DATA_TAB);
    var showVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_VOTERS_TAB);
    // The tab reads with messaging-config-read; with write only, it says so.
    var showMessaging = showMessagingTab(function (permissions) { return authContext.isAuthorized(true, authContext.tenantId, permissions); }, isElectionEventLocked);
    var showAreas = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_AREAS_TAB);
    var showKeys = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, [
            IPermissions.ADMIN_CEREMONY,
            IPermissions.TRUSTEE_CEREMONY,
        ]) &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_KEYS_TAB);
    var showTally = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, [
            IPermissions.TALLY_READ,
            IPermissions.TALLY_START,
        ]) &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_TALLY_TAB);
    var showTallySheetImports = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.TALLY_SHEET_IMPORT_VIEW);
    var showPublish = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_PUBLISH_TAB);
    var showLogs = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_LOGS_TAB);
    var showTasksExecution = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_TASKS_TAB);
    var showEvents = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_SCHEDULED_TAB);
    var showReports = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_REPORTS_TAB);
    var showApprovalsExecution = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_APPROVALS_TAB);
    // The tab and at least one of its sub-tabs; each sub-tab checks its own read permission.
    var showSignatures = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_SIGNATURES_TAB) &&
        authContext.isAuthorized(true, authContext.tenantId, [
            IPermissions.SIGNING_RULES_READ,
            IPermissions.SIGNING_CERTIFICATES_READ,
            IPermissions.SIGNING_REQUESTS_READ,
        ]);
    var showCAs = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_CAS_TAB) &&
        ((_b = record === null || record === void 0 ? void 0 : record.presentation) === null || _b === void 0 ? void 0 : _b.voter_certificate_policy) === EVoterCertificatePolicy.ENABLED;
    var isTelephoneChannelEnabled = (_d = (_c = record === null || record === void 0 ? void 0 : record.voting_channels) === null || _c === void 0 ? void 0 : _c.telephone) !== null && _d !== void 0 ? _d : false;
    var showIvr = isTelephoneChannelEnabled &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_EVENT_IVR_TAB);
    // -----------------------------------------------------------------
    // Build tabs with 100% stable references
    // -----------------------------------------------------------------
    var tabs = useMemo(function () {
        var result = [];
        // Dashboard
        if (showDashboard) {
            result.push({
                label: t("electionEventScreen.tabs.dashboard"),
                component: DashboardTab,
                props: { refreshRef: refreshRef, handleChildMount: handleChildMount },
            });
        }
        // Data
        if (showData) {
            result.push({
                id: "data",
                label: t("electionEventScreen.tabs.data"),
                component: ElectionEventDataTab,
            });
        }
        // IVR
        if (showIvr) {
            result.push({ label: t("electionEventScreen.tabs.ivr"), component: IvrTab });
        }
        // Localization
        if (showTextData) {
            result.push({
                label: t("electionEventScreen.tabs.localization"),
                component: LocalizationTab,
            });
        }
        // Voters
        if (showVoters) {
            result.push({ label: t("electionEventScreen.tabs.voters"), component: VotersTab });
        }
        // Messaging
        if (showMessaging) {
            result.push({ label: t("messagingEvent.tab"), component: MessagingTab });
        }
        // Areas
        if (showAreas) {
            result.push({ label: t("electionEventScreen.tabs.areas"), component: AreasTab });
        }
        // Keys
        if (showKeys) {
            result.push({
                label: t("electionEventScreen.tabs.keys"),
                component: KeysTab,
                props: { showKeysList: showKeysList },
                action: function () { return setShowKeysList(uuidv4()); },
            });
        }
        // Signatures
        if (showSignatures) {
            result.push({
                label: t("signing.tab.title"),
                component: SignaturesTab,
            });
        }
        // CAs
        if (showCAs) {
            result.push({ label: t("electionEventScreen.tabs.cas"), component: CAsTab });
        }
        // Tally
        if (showTally) {
            result.push({
                id: "tally",
                label: t("electionEventScreen.tabs.tally"),
                component: TallyTab,
                action: function () { return setTallyId(null); },
            });
        }
        if (showTallySheetImports) {
            result.push({
                id: "tally-sheet-imports",
                label: t("electionEventScreen.tabs.tallySheetImports"),
                component: TallySheetImportsTab,
                props: {
                    openImportId: pendingTallySheetImportId,
                    onOpenImportHandled: clearPendingTallySheetImportId,
                },
            });
        }
        // Publish
        if (showPublish) {
            result.push({
                id: "publish",
                label: t("electionEventScreen.tabs.publish"),
                component: PublishTab,
                props: { showList: showPublishList },
                action: function () { return setShowPublishList(uuidv4()); },
            });
        }
        // Tasks
        if (showTasksExecution) {
            result.push({
                label: t("electionEventScreen.tabs.tasks"),
                component: TasksTab,
                props: { showList: showTaskList },
                action: function () { return setShowTaskList(uuidv4()); },
            });
        }
        // Logs
        if (showLogs) {
            result.push({
                id: LOGS_TAB_ID,
                label: t("electionEventScreen.tabs.logs"),
                component: LogsTab,
            });
        }
        // Events
        if (showEvents) {
            result.push({ label: t("electionEventScreen.tabs.events"), component: EventsTab });
        }
        // Reports
        if (showReports) {
            result.push({ label: t("electionEventScreen.tabs.reports"), component: ReportsTab });
        }
        // Approvals
        if (showApprovalsExecution) {
            result.push({
                label: t("electionEventScreen.tabs.approvals"),
                component: ApprovalsTab,
                props: { showList: showApprovalList },
                action: function () {
                    setShowApprovalList(uuidv4());
                    localStorage.setItem("approvals_status_filter", "pending");
                },
            });
        }
        return result;
    }, [
        showDashboard,
        showData,
        showTextData,
        showVoters,
        showMessaging,
        showAreas,
        showKeys,
        showTally,
        showTallySheetImports,
        showPublish,
        showLogs,
        showTasksExecution,
        showEvents,
        showReports,
        showApprovalsExecution,
        showSignatures,
        showCAs,
        showIvr,
        t,
        showKeysList,
        showPublishList,
        showTaskList,
        showApprovalList,
        refreshRef,
        handleChildMount,
        setTallyId,
        pendingTallySheetImportId,
        clearPendingTallySheetImportId,
    ]);
    var tallySheetImportsTabIndex = tabs.findIndex(function (tab) { return tab.id === "tally-sheet-imports"; });
    var dataTabIndex = tabs.findIndex(function (tab) { return tab.id === "data"; });
    useEffect(function () {
        if (new URLSearchParams(location.search).get("tabId") === "data" && dataTabIndex >= 0) {
            setSelectedTab(dataTabIndex);
        }
    }, [location.search, dataTabIndex]);
    var logsTabIndex = tabs.findIndex(function (tab) { return tab.id === LOGS_TAB_ID; });
    // The Messaging tab's Failed counts open Logs with `tabId=logs` and a filter.
    useEffect(function () {
        var tabId = new URLSearchParams(location.search).get("tabId");
        if (tabId === LOGS_TAB_ID && logsTabIndex >= 0) {
            setSelectedTab(logsTabIndex);
        }
    }, [location.search, logsTabIndex]);
    var publishTabIndex = tabs.findIndex(function (tab) { return tab.id === "publish"; });
    // Publish's re-authentication returns with `tabId=publish`: tab positions depend on permissions.
    useEffect(function () {
        var tabId = new URLSearchParams(location.search).get("tabId");
        if (tabId === "publish" && publishTabIndex >= 0) {
            setSelectedTab(publishTabIndex);
        }
    }, [location.search, publishTabIndex]);
    useEffect(function () {
        var searchParams = new URLSearchParams(location.search);
        var tabId = searchParams.get("tabId");
        var importId = searchParams.get("tallySheetImportId");
        if (tallySheetImportsTabIndex < 0) {
            return;
        }
        if (tabId === "tally-sheet-imports" || importId) {
            setSelectedTab(tallySheetImportsTabIndex);
        }
        if (importId) {
            setPendingTallySheetImportId(importId);
        }
    }, [location.search, tallySheetImportsTabIndex]);
    if (!record) {
        return (_jsx(Box, { children: _jsx(CircularProgress, {}) }));
    }
    return (_jsxs(Box, { sx: {
            maxWidth: "calc(100vw - ".concat(open ? "352px" : "96px", ")"),
            bgcolor: "background.paper",
        }, className: "events-box", children: [_jsx(ElectionHeader, { title: aliasRenderer(record), subtitle: "electionEventScreen.common.subtitle" }), isElectionEventLocked && _jsx(LockdownBanner, { event: record }), _jsx(Box, { sx: { bgcolor: "background.paper" }, children: _jsx(RecordContextProvider, { value: record, children: _jsx(EventTimeZoneProvider, { event: record, children: tabs.length ? (_jsx(Tabs, { elements: tabs, selectedTab: selectedTab, onSelectedTabChange: setSelectedTab })) : (_jsx(Alert, { severity: "info", sx: { m: 2 }, children: t("electionEventScreen.noAccessibleTabs") })) }) }) })] }));
};
